import { resolveApiBaseUrl } from "./map-runtime-urls.js";
import { mapText } from "./map-i18n.js";
import {
  IMPERIAL_FISHING_DELIVERY_ICON_PATH,
  TRADE_NPC_CATALOG_SCHEMA,
  TRADE_NPC_CATALOG_VERSION,
  TRADE_NPC_CLASS_BARTER,
  TRADE_NPC_CLASS_IMPERIAL_FISHING_DELIVERY,
  TRADE_NPC_CLASS_TRADER,
  TRADE_NPC_LAYER_ID,
  normalizeTradeNpcClass,
} from "./map-trade-npc-contract.js";

const DEFAULT_TRADE_NPCS_MAP_PATH = "/api/v1/trade_npcs/map";
const TRADE_SALE_PROJECTION_SCHEMA = "fishystuff.trade_sale_projection";
const TRADE_SALE_PROJECTION_VERSION = 1;
const ORIGIN_TARGET_KEY = "origin_node";

let cachedTradeNpcMapCatalog = null;
let cachedTradeNpcMapPromise = null;
const TRADE_NPC_BY_KEY_CACHE = new WeakMap();

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function finiteNumber(value) {
  if (value == null || (typeof value === "string" && !value.trim())) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function integerValue(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeLookupKey(value) {
  return trimString(value).toLowerCase().replace(/\s+/g, " ");
}

function slugifyLookupKey(value) {
  const normalized = normalizeLookupKey(value);
  const ascii =
    typeof normalized.normalize === "function"
      ? normalized.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
      : normalized;
  return ascii
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeSelectorList(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = trimString(value);
    const lookupKey = normalizeLookupKey(normalized);
    if (!lookupKey || seen.has(lookupKey)) {
      continue;
    }
    seen.add(lookupKey);
    next.push(normalized);
  }
  return next;
}

function regionIdFromLabel(value) {
  const label = trimString(value);
  if (!label) {
    return null;
  }
  const match = label.match(/\bR(\d+)\b/i) || label.match(/^\((\d+)\|/);
  if (!match) {
    return null;
  }
  const regionId = Number.parseInt(match[1], 10);
  return Number.isInteger(regionId) && regionId >= 0 ? regionId : null;
}

function normalizeCoordinatePair(source) {
  if (!isPlainObject(source)) {
    return { worldX: null, worldZ: null };
  }
  return {
    worldX: finiteNumber(source.worldX ?? source.world_x),
    worldZ: finiteNumber(source.worldZ ?? source.world_z),
  };
}

function normalizeRegionRef(source) {
  if (!isPlainObject(source)) {
    return {
      regionId: null,
      regionName: "",
      waypointId: null,
      waypointName: "",
      worldX: null,
      worldZ: null,
    };
  }
  const coords = normalizeCoordinatePair(source);
  return {
    regionId: integerValue(source.regionId ?? source.region_id),
    regionName: trimString(source.regionName ?? source.region_name),
    waypointId: integerValue(source.waypointId ?? source.waypoint_id),
    waypointName: trimString(source.waypointName ?? source.waypoint_name),
    ...coords,
  };
}

function normalizeTradeNpcKnowledge(source) {
  if (!isPlainObject(source)) {
    return null;
  }
  const cardKey = integerValue(source.cardKey);
  const sourcePicturePath = trimString(source.sourcePicturePath);
  const imagePath = trimString(source.imagePath);
  if (cardKey == null || cardKey <= 0) {
    return null;
  }
  return {
    cardKey,
    sourcePicturePath,
    imagePath,
  };
}

function formatRegionRef(region) {
  if (!region) {
    return "";
  }
  if (region.regionName && region.regionId != null) {
    return `${region.regionName} (R${region.regionId})`;
  }
  if (region.regionName) {
    return region.regionName;
  }
  if (region.regionId != null) {
    return `R${region.regionId}`;
  }
  return "";
}

function normalizeDetailFacts(sample) {
  if (!Array.isArray(sample?.detailSections)) {
    return [];
  }
  const facts = [];
  for (const section of sample.detailSections) {
    if (!isPlainObject(section) || trimString(section.kind) !== "facts") {
      continue;
    }
    for (const fact of Array.isArray(section.facts) ? section.facts : []) {
      if (!isPlainObject(fact)) {
        continue;
      }
      facts.push({
        key: trimString(fact.key),
        label: trimString(fact.label),
        value: trimString(fact.value),
        icon: trimString(fact.icon),
      });
    }
  }
  return facts;
}

function selectedOriginTarget(sample) {
  const targets = Array.isArray(sample?.targets) ? sample.targets : [];
  return targets.find((target) => trimString(target?.key) === ORIGIN_TARGET_KEY) || null;
}

export function selectedTradeOriginFromLayerSamples(layerSamples) {
  const regionSample = (Array.isArray(layerSamples) ? layerSamples : []).find(
    (sample) => trimString(sample?.layerId) === "regions",
  );
  if (!regionSample) {
    return null;
  }
  const facts = normalizeDetailFacts(regionSample);
  const originFact = facts.find((fact) => fact.key === "origin_region") || null;
  if (!originFact?.value) {
    return null;
  }
  const target = selectedOriginTarget(regionSample);
  const coords = normalizeCoordinatePair(target);
  return {
    regionId: regionIdFromLabel(originFact.value),
    label: originFact.value,
    worldX: coords.worldX,
    worldZ: coords.worldZ,
  };
}

function normalizeTradeNpcFeature(feature) {
  if (!isPlainObject(feature)) {
    return null;
  }
  const properties = isPlainObject(feature.properties) ? feature.properties : feature;
  const geometryCoordinates = Array.isArray(feature.geometry?.coordinates)
    ? feature.geometry.coordinates
    : [];
  const sellOrigin = normalizeRegionRef(
    properties.sellOrigin ??
      properties.sellDestinationTradeOrigin ??
      properties.sell_destination_trade_origin,
  );
  const assignedRegion = normalizeRegionRef(properties.assignedRegion ?? properties.assigned_region);
  const spawn = normalizeRegionRef(properties.spawn ?? properties.npcSpawn ?? properties.npc_spawn);
  const fallbackPoint = {
    worldX: finiteNumber(geometryCoordinates[0]),
    worldZ: finiteNumber(geometryCoordinates[1]),
  };
  const npcName = trimString(
    properties.npcName ?? properties.npc_name ?? properties.name ?? properties.label,
  );
  if (!npcName) {
    return null;
  }
  const knowledge = normalizeTradeNpcKnowledge(properties.knowledge);
  const npcClass = normalizeTradeNpcClass(properties.class);
  if (!npcClass) {
    return null;
  }
  return {
    id: trimString(properties.id) || trimString(feature.id) || npcName,
    npcKey: integerValue(properties.npcKey ?? properties.npc_key),
    npcName,
    class: npcClass,
    classLabel: trimString(properties.classLabel ?? properties.class_label),
    layerId: TRADE_NPC_LAYER_ID,
    sourceTags: Array.isArray(properties.sourceTags ?? properties.source_tags)
      ? (properties.sourceTags ?? properties.source_tags).map(trimString).filter(Boolean)
      : [],
    knowledge,
    imagePath: knowledge?.imagePath || "",
    sellOrigin,
    assignedRegion,
    spawn: {
      ...spawn,
      worldX: spawn.worldX ?? fallbackPoint.worldX,
      worldZ: spawn.worldZ ?? fallbackPoint.worldZ,
    },
    spawnRegionLabel: trimString(properties.npcRegionLabel) || formatRegionRef(spawn),
    sellOriginLabel: trimString(properties.sellOriginLabel) || formatRegionRef(sellOrigin),
    assignedRegionLabel: trimString(properties.assignedRegionLabel) || formatRegionRef(assignedRegion),
  };
}

export function tradeNpcForKey(rawCatalog, npcKey) {
  const normalizedNpcKey = integerValue(npcKey);
  if (
    normalizedNpcKey == null ||
    normalizedNpcKey <= 0 ||
    !isPlainObject(rawCatalog)
  ) {
    return null;
  }
  const sourceFeatures = Array.isArray(rawCatalog.features) ? rawCatalog.features : [];
  const cached = TRADE_NPC_BY_KEY_CACHE.get(rawCatalog);
  if (
    cached &&
    cached.sourceFeatures === sourceFeatures &&
    cached.sourceLength === sourceFeatures.length
  ) {
    return cached.byNpcKey.get(normalizedNpcKey) || null;
  }
  const catalog = normalizeTradeNpcMapCatalog(rawCatalog);
  const byNpcKey = new Map(
    catalog.features
      .filter((npc) => Number.isInteger(npc.npcKey) && npc.npcKey > 0)
      .map((npc) => [npc.npcKey, npc]),
  );
  TRADE_NPC_BY_KEY_CACHE.set(rawCatalog, {
    sourceFeatures,
    sourceLength: sourceFeatures.length,
    byNpcKey,
  });
  return byNpcKey.get(normalizedNpcKey) || null;
}

export function resolveTradeNpcImageUrl(npc) {
  const imagePath = trimString(npc?.imagePath ?? npc?.knowledge?.imagePath);
  if (!imagePath) {
    return "";
  }
  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }
  const resolver = globalThis.window?.__fishystuffResolveCdnUrl;
  return typeof resolver === "function" ? trimString(resolver(imagePath)) : imagePath;
}

export function resolveTradeNpcBadgeImageUrl(npc) {
  if (normalizeTradeNpcClass(npc?.class) !== TRADE_NPC_CLASS_IMPERIAL_FISHING_DELIVERY) {
    return "";
  }
  const resolver = globalThis.window?.__fishystuffResolveCdnUrl;
  return typeof resolver === "function"
    ? trimString(resolver(IMPERIAL_FISHING_DELIVERY_ICON_PATH))
    : IMPERIAL_FISHING_DELIVERY_ICON_PATH;
}

export function normalizeTradeNpcMapCatalog(raw) {
  const source = isPlainObject(raw) ? raw : {};
  return {
    metadata: isPlainObject(source.metadata) ? cloneJson(source.metadata) : {},
    features: Array.isArray(source.features)
      ? source.features.map(normalizeTradeNpcFeature).filter(Boolean)
      : [],
  };
}

function tradeSaleProjectionFromCatalog(catalog) {
  const source = catalog?.metadata?.tradeSaleProjection;
  const schema = trimString(source?.schema);
  const version = integerValue(source?.version);
  const distanceBonusScale = finiteNumber(source?.distanceBonusScale);
  const maximumDistanceBonusPercent = finiteNumber(source?.maximumDistanceBonusPercent);
  const standardSaleModifierMultiplier = finiteNumber(
    source?.standardSaleModifierMultiplier,
  );
  const imperialFishingSaleMultiplier = finiteNumber(
    source?.imperialFishingSaleMultiplier,
  );
  if (
    schema !== TRADE_SALE_PROJECTION_SCHEMA
    || version !== TRADE_SALE_PROJECTION_VERSION
    || distanceBonusScale == null
    || distanceBonusScale <= 0
    || maximumDistanceBonusPercent == null
    || maximumDistanceBonusPercent < 0
    || standardSaleModifierMultiplier == null
    || standardSaleModifierMultiplier < 0
    || imperialFishingSaleMultiplier == null
    || imperialFishingSaleMultiplier < 0
  ) {
    throw new Error(
      `Unsupported trade sale projection ${schema || "missing"} v${version ?? "missing"}`,
    );
  }
  return {
    distanceBonusScale,
    maximumDistanceBonusPercent,
    standardSaleModifierMultiplier,
    imperialFishingSaleMultiplier,
  };
}

function validateTradeNpcMapCatalogContract(raw) {
  const metadata = isPlainObject(raw?.metadata) ? raw.metadata : {};
  const schema = trimString(metadata.schema);
  const version = integerValue(metadata.version);
  if (schema !== TRADE_NPC_CATALOG_SCHEMA || version !== TRADE_NPC_CATALOG_VERSION) {
    throw new Error(
      `Unsupported trade NPC map catalog contract ${schema || "missing"} v${version ?? "missing"}; expected ${TRADE_NPC_CATALOG_SCHEMA} v${TRADE_NPC_CATALOG_VERSION}`,
    );
  }
  tradeSaleProjectionFromCatalog({ metadata });
  for (const [index, feature] of (Array.isArray(raw?.features) ? raw.features : []).entries()) {
    const properties = isPlainObject(feature?.properties) ? feature.properties : feature;
    if (!normalizeTradeNpcClass(properties?.class)) {
      throw new Error(`Trade NPC map catalog feature ${index} has an unsupported class`);
    }
  }
}

function distanceBonusPercent(origin, destination, projection) {
  if (
    !Number.isFinite(origin?.worldX) ||
    !Number.isFinite(origin?.worldZ) ||
    !Number.isFinite(destination?.sellOrigin?.worldX) ||
    !Number.isFinite(destination?.sellOrigin?.worldZ)
  ) {
    return null;
  }
  const dx = origin.worldX - destination.sellOrigin.worldX;
  const dz = origin.worldZ - destination.sellOrigin.worldZ;
  return Math.hypot(dx, dz) * projection.distanceBonusScale;
}

function focusWorldPointForNpc(destination) {
  if (!Number.isFinite(destination?.spawn?.worldX) || !Number.isFinite(destination?.spawn?.worldZ)) {
    return null;
  }
  return {
    elementKind: "npc",
    worldX: destination.spawn.worldX,
    worldZ: destination.spawn.worldZ,
    pointKind: "waypoint",
    pointLabel: destination.npcName,
  };
}

function tradeNpcMatchesSelector(destination, selector) {
  const normalizedSelector = normalizeLookupKey(selector);
  const slugSelector = slugifyLookupKey(selector);
  const numericSelector = Number.parseInt(selector, 10);
  if (Number.isInteger(numericSelector) && destination.npcKey === numericSelector) {
    return "exact";
  }
  const normalizedName = normalizeLookupKey(destination.npcName);
  const slugName = slugifyLookupKey(destination.npcName);
  if (normalizedName === normalizedSelector || slugName === slugSelector) {
    return "exact";
  }
  if (
    normalizedSelector &&
    (normalizedName.includes(normalizedSelector) ||
      (slugSelector && slugName.includes(slugSelector)))
  ) {
    return "partial";
  }
  return "";
}

export function tradeNpcFocusTargetForSelectors(selectors, rawCatalog) {
  const catalog = normalizeTradeNpcMapCatalog(rawCatalog);
  const normalizedSelectors = normalizeSelectorList(selectors);
  for (const selector of normalizedSelectors) {
    const exact = catalog.features.find((destination) => (
      tradeNpcMatchesSelector(destination, selector) === "exact" &&
      focusWorldPointForNpc(destination)
    ));
    if (exact) {
      return focusWorldPointForNpc(exact);
    }
    const partialMatches = catalog.features.filter((destination) => (
      tradeNpcMatchesSelector(destination, selector) === "partial" &&
      focusWorldPointForNpc(destination)
    ));
    if (partialMatches.length === 1) {
      return focusWorldPointForNpc(partialMatches[0]);
    }
  }
  return null;
}

export function formatTradeDistanceBonus(value) {
  const normalized = finiteNumber(value);
  return normalized == null ? "" : `${Math.max(0, normalized).toFixed(1)}%`;
}
export function formatTradeSaleMultiplier(value) {
  const normalized = finiteNumber(value);
  if (normalized == null) {
    return "";
  }
  const multiplier = Math.round(Math.max(0, normalized) * 1_000) / 1_000;
  return `${multiplier}x`;
}

function tradeSaleMultiplier(destination, distanceBonus, projection) {
  if (destination?.class === TRADE_NPC_CLASS_IMPERIAL_FISHING_DELIVERY) {
    return {
      kind: "fixed",
      value: projection.imperialFishingSaleMultiplier,
    };
  }
  if (destination?.class !== TRADE_NPC_CLASS_TRADER) {
    return null;
  }
  const distanceMultiplier = 1
    + Math.min(distanceBonus, projection.maximumDistanceBonusPercent) / 100;
  return {
    kind: "calculator-final",
    value: distanceMultiplier * projection.standardSaleModifierMultiplier,
  };
}


export function tradeManagerRowsForOrigin(layerSamples, rawCatalog) {
  const origin = selectedTradeOriginFromLayerSamples(layerSamples);
  const catalog = normalizeTradeNpcMapCatalog(rawCatalog);
  if (
    !origin
    || !Number.isFinite(origin.worldX)
    || !Number.isFinite(origin.worldZ)
    || catalog.features.length === 0
  ) {
    return [];
  }
  const projection = tradeSaleProjectionFromCatalog(catalog);
  return catalog.features
    .filter((destination) => destination.class !== TRADE_NPC_CLASS_BARTER)
    .map((destination) => {
      const distanceBonus = distanceBonusPercent(origin, destination, projection);
      if (distanceBonus == null) {
        return null;
      }
      const saleMultiplier = tradeSaleMultiplier(destination, distanceBonus, projection);
      if (!saleMultiplier) {
        return null;
      }
      return {
        ...destination,
        distanceBonus,
        distanceBonusText: formatTradeDistanceBonus(distanceBonus),
        saleMultiplier: saleMultiplier.value,
        saleMultiplierKind: saleMultiplier.kind,
        saleMultiplierText: formatTradeSaleMultiplier(saleMultiplier.value),
        maximumDistanceBonusPercent: projection.maximumDistanceBonusPercent,
        imageUrl: resolveTradeNpcImageUrl(destination),
        badgeImageUrl: resolveTradeNpcBadgeImageUrl(destination),
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const distance = right.distanceBonus - left.distanceBonus;
      if (distance !== 0) {
        return distance;
      }
      const name = left.npcName.localeCompare(right.npcName);
      if (name !== 0) {
        return name;
      }
      return left.sellOriginLabel.localeCompare(right.sellOriginLabel);
    });
}

function tradeManagerListRow(row) {
  const focusWorldPoint = focusWorldPointForNpc(row);
  return {
    id: row.id,
    npcKey: row.npcKey,
    npcName: row.npcName,
    imageUrl: row.imageUrl,
    badgeImageUrl: row.badgeImageUrl,
    class: row.class,
    classLabel: row.classLabel,
    layerId: row.layerId,
    regionLabel: row.sellOriginLabel || row.assignedRegionLabel,
    distanceBonus: row.distanceBonus,
    distanceBonusText: row.distanceBonusText,
    saleMultiplier: row.saleMultiplier,
    saleMultiplierKind: row.saleMultiplierKind,
    saleMultiplierText: row.saleMultiplierText,
    action: focusWorldPoint
      ? {
          kind: "focus-world-point",
          focusWorldPoint,
        }
      : null,
  };
}

export function tradeManagerListForOrigin(layerSamples, rawCatalog, { status = "idle" } = {}) {
  const origin = selectedTradeOriginFromLayerSamples(layerSamples);
  if (!origin) {
    return null;
  }
  if (status === "loading") {
    return { state: "loading", count: 0, groups: [] };
  }
  if (status === "error") {
    return { state: "error", count: 0, groups: [] };
  }
  const rows = tradeManagerRowsForOrigin(layerSamples, rawCatalog);
  if (!rows.length) {
    return { state: "empty", count: 0, groups: [] };
  }
  const maximumBonusRows = [];
  const belowMaximumBonusRows = [];
  for (const row of rows) {
    const destination = tradeManagerListRow(row);
    if (row.distanceBonus >= row.maximumDistanceBonusPercent) {
      maximumBonusRows.push(destination);
    } else {
      belowMaximumBonusRows.push(destination);
    }
  }
  const groups = [
    { id: "maximum-bonus", rows: maximumBonusRows },
    { id: "below-maximum-bonus", rows: belowMaximumBonusRows },
  ].filter((group) => group.rows.length > 0);
  return { state: "ready", count: rows.length, groups };
}

export async function loadTradeNpcMapCatalog({
  fetchImpl = globalThis.fetch,
  locationLike = globalThis.window?.location,
  force = false,
} = {}) {
  if (!force && cachedTradeNpcMapCatalog) {
    return cachedTradeNpcMapCatalog;
  }
  if (!force && cachedTradeNpcMapPromise) {
    return cachedTradeNpcMapPromise;
  }
  const baseUrl = resolveApiBaseUrl({ locationLike });
  cachedTradeNpcMapPromise = Promise.resolve()
    .then(() => fetchImpl(`${baseUrl}${DEFAULT_TRADE_NPCS_MAP_PATH}`))
    .then(async (response) => {
      if (!response?.ok) {
        throw new Error(mapText("info.trade.request_failed", { status: response?.status || 0 }));
      }
      const payload = await response.json();
      validateTradeNpcMapCatalogContract(payload);
      const catalog = normalizeTradeNpcMapCatalog(payload);
      cachedTradeNpcMapCatalog = catalog;
      return catalog;
    })
    .finally(() => {
      cachedTradeNpcMapPromise = null;
    });
  return cachedTradeNpcMapPromise;
}
